package com.sweetlime.bingeamigo.presentation.features.tvdetail

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Star
import androidx.compose.material.icons.outlined.CheckCircle
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.sweetlime.bingeamigo.model.Episode
import com.sweetlime.bingeamigo.model.LibraryEpisode
import com.sweetlime.bingeamigo.model.SeasonDetail
import com.sweetlime.bingeamigo.model.SeasonSummary
import com.sweetlime.bingeamigo.model.TvShow
import com.sweetlime.bingeamigo.presentation.features.library.TvLibraryViewModel
import com.sweetlime.bingeamigo.presentation.ui.BrandColors
import com.sweetlime.bingeamigo.util.format1f
import io.kamel.image.KamelImage
import io.kamel.image.asyncPainterResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun SeasonDetailContent(
    tvShow: TvShow,
    season: SeasonSummary,
    showPosterFallback: String,
    totalEpisodesInShow: Int,
    onBackClick: () -> Unit,
    onEpisodeClick: (Episode) -> Unit = {},
    viewModel: SeasonDetailViewModel = koinViewModel()
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    LaunchedEffect(tvShow.id, season.seasonNumber) {
        viewModel.load(tvShow.id, season.seasonNumber)
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFF121212))
    ) {
        when (state) {
            is SeasonDetailState.Loading -> {
                CircularProgressIndicator(modifier = Modifier.align(Alignment.Center))
                TopBackButton(onBackClick)
            }

            is SeasonDetailState.Error -> {
                Text(
                    text = (state as SeasonDetailState.Error).message,
                    color = Color.White,
                    modifier = Modifier.align(Alignment.Center)
                )
                TopBackButton(onBackClick)
            }

            is SeasonDetailState.Success -> {
                SeasonDetailLoaded(
                    tvShow = tvShow,
                    season = season,
                    detail = (state as SeasonDetailState.Success).seasonDetail,
                    showPosterFallback = showPosterFallback,
                    totalEpisodesInShow = totalEpisodesInShow,
                    onBackClick = onBackClick,
                    onEpisodeClick = onEpisodeClick
                )
            }
        }
    }
}

@Composable
private fun SeasonDetailLoaded(
    tvShow: TvShow,
    season: SeasonSummary,
    detail: SeasonDetail,
    showPosterFallback: String,
    totalEpisodesInShow: Int,
    onBackClick: () -> Unit,
    onEpisodeClick: (Episode) -> Unit,
    tvLibraryViewModel: TvLibraryViewModel = koinViewModel()
) {
    val watchedEpisodes by tvLibraryViewModel.watchedEpisodesForShow(tvShow.id.toString())
        .collectAsStateWithLifecycle(emptyList())

    @OptIn(ExperimentalTime::class)
    val todayStr = remember {
        val ms = Clock.System.now().toEpochMilliseconds()
        val days = (ms / 86_400_000L).toInt()
        var n = days + 719468
        val era = if (n >= 0) n else n - 146096
        val doe = era % 146097
        val yoe = (doe - doe / 1460 + doe / 36524 - doe / 146096) / 365
        val y = yoe + (era / 146097) * 400
        val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)
        val mp = (5 * doy + 2) / 153
        val d = doy - (153 * mp + 2) / 5 + 1
        val m = if (mp < 10) mp + 3 else mp - 9
        val year = if (m <= 2) y + 1 else y
        "${year}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"
    }

    var showMarkAllDialog by remember { mutableStateOf(false) }

    val posterUrl = if (season.posterPath != null) {
        "https://image.tmdb.org/t/p/w342${season.posterPath}"
    } else {
        showPosterFallback
    }

    LazyColumn(modifier = Modifier.fillMaxSize()) {

        // Season header
        item {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color(0xFF1A1A1A))
                    .statusBarsPadding()
                    .padding(top = 56.dp, start = 20.dp, end = 20.dp, bottom = 20.dp)
            ) {
                Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                    KamelImage(
                        resource = asyncPainterResource(posterUrl),
                        contentDescription = season.name,
                        contentScale = ContentScale.Crop,
                        modifier = Modifier
                            .width(90.dp)
                            .aspectRatio(2f / 3f)
                            .clip(RoundedCornerShape(10.dp))
                    )

                    Column(
                        modifier = Modifier.weight(1f),
                        verticalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        Text(
                            text = season.name,
                            color = Color.White,
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold
                        )

                        val year = season.airDate.take(4)
                        if (year.isNotBlank()) {
                            Text(text = year, color = Color.Gray, style = MaterialTheme.typography.bodyMedium)
                        }

                        Text(
                            text = "${detail.episodes.size} episodes",
                            color = Color.Gray,
                            style = MaterialTheme.typography.bodyMedium
                        )

                        if (season.voteAverage > 0.0) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(4.dp)
                            ) {
                                Icon(
                                    Icons.Filled.Star,
                                    contentDescription = null,
                                    tint = BrandColors.TextSecondary,
                                    modifier = Modifier.size(14.dp)
                                )
                                Text(
                                    text = (season.voteAverage / 2).format1f(),
                                    color = BrandColors.TextSecondary,
                                    style = MaterialTheme.typography.bodyMedium,
                                    fontWeight = FontWeight.Bold
                                )
                            }
                        }

                        if (detail.overview.isNotBlank()) {
                            Spacer(modifier = Modifier.height(4.dp))
                            Text(
                                text = detail.overview,
                                color = Color.LightGray,
                                style = MaterialTheme.typography.bodySmall,
                                lineHeight = 18.sp,
                                maxLines = 4,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                    }
                }
            }
        }

        // Episodes title + mark-all button
        item {
            val unwatchedEpisodes = detail.episodes.filter { episode ->
                watchedEpisodes.find { it.id == episode.id }?.watched != true &&
                    !(episode.airDate.length == 10 && episode.airDate > todayStr)
            }
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(start = 20.dp, top = 20.dp, bottom = 8.dp, end = 8.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "Episodes",
                    color = Color.White,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold
                )
                if (unwatchedEpisodes.isNotEmpty()) {
                    TextButton(onClick = { showMarkAllDialog = true }) {
                        Text(
                            text = "Mark all watched",
                            color = BrandColors.Primary,
                            style = MaterialTheme.typography.labelMedium,
                            fontWeight = FontWeight.SemiBold
                        )
                    }
                }
            }

            if (showMarkAllDialog) {
                AlertDialog(
                    onDismissRequest = { showMarkAllDialog = false },
                    title = { Text("Mark all watched?", color = Color.White) },
                    text = {
                        Text(
                            text = "Mark all ${unwatchedEpisodes.size} unwatched episodes in ${season.name} as watched.",
                            color = BrandColors.TextSecondary
                        )
                    },
                    confirmButton = {
                        TextButton(onClick = {
                            showMarkAllDialog = false
                            val lastEpisode = unwatchedEpisodes.maxByOrNull { it.episodeNumber }
                            if (lastEpisode != null) {
                                tvLibraryViewModel.markAllSeasonWatched(
                                    tvShow = tvShow,
                                    seasonEpisodes = unwatchedEpisodes.map { ep ->
                                        LibraryEpisode(
                                            id = ep.id,
                                            watched = true,
                                            seasonNumber = ep.seasonNumber,
                                            episodeNumber = ep.episodeNumber
                                        )
                                    },
                                    lastEpisode = LibraryEpisode(
                                        id = lastEpisode.id,
                                        watched = true,
                                        seasonNumber = lastEpisode.seasonNumber,
                                        episodeNumber = lastEpisode.episodeNumber
                                    ),
                                    totalEpisodesInShow = totalEpisodesInShow,
                                    lastEpisodeName = lastEpisode.name,
                                    lastEpisodeStillPath = lastEpisode.stillPath.orEmpty()
                                )
                            }
                        }) {
                            Text("Mark all watched", color = BrandColors.Primary, fontWeight = FontWeight.Bold)
                        }
                    },
                    dismissButton = {
                        TextButton(onClick = { showMarkAllDialog = false }) {
                            Text("Cancel", color = BrandColors.TextSecondary)
                        }
                    },
                    containerColor = Color(0xFF1A1A1C)
                )
            }
        }

        // Episode rows
        itemsIndexed(detail.episodes) { index, episode ->
            val libraryEpisode = watchedEpisodes.find { it.id == episode.id }
            val isWatched = libraryEpisode?.watched == true
            val userRating = libraryEpisode?.userRating ?: 0.0
            val isFuture = episode.airDate.length == 10 && episode.airDate > todayStr
            EpisodeRow(
                episode = episode,
                isWatched = isWatched,
                isFuture = isFuture,
                userRating = userRating,
                onToggleWatched = {
                    if (isWatched) {
                        tvLibraryViewModel.markEpisodeUnwatched(tvShow, episode.id)
                    } else {
                        tvLibraryViewModel.markEpisodeWatched(tvShow, LibraryEpisode(id = episode.id, watched = true, seasonNumber = episode.seasonNumber, episodeNumber = episode.episodeNumber), totalEpisodesInShow, episodeName = episode.name, episodeStillPath = episode.stillPath.orEmpty())
                    }
                },
                onEpisodeClick = { onEpisodeClick(episode) }
            )
            if (index < detail.episodes.lastIndex) {
                HorizontalDivider(
                    modifier = Modifier.padding(horizontal = 20.dp),
                    color = Color.White.copy(alpha = 0.06f)
                )
            }
        }

        item { Spacer(modifier = Modifier.height(120.dp)) }
    }

    TopBackButton(onBackClick)
}

@Composable
private fun EpisodeRow(
    episode: Episode,
    isWatched: Boolean,
    isFuture: Boolean,
    userRating: Double,
    onToggleWatched: () -> Unit,
    onEpisodeClick: () -> Unit
) {
    var isLoading by remember { mutableStateOf(false) }
    LaunchedEffect(isWatched) { isLoading = false }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onEpisodeClick() }
            .padding(horizontal = 20.dp, vertical = 12.dp),
        horizontalArrangement = Arrangement.spacedBy(14.dp),
        verticalAlignment = Alignment.Top
    ) {
        // Still image
        Box(
            modifier = Modifier
                .width(120.dp)
                .aspectRatio(16f / 9f)
                .clip(RoundedCornerShape(8.dp))
                .background(Color(0xFF2A2A2A)),
            contentAlignment = Alignment.Center
        ) {
            if (episode.stillPath != null) {
                KamelImage(
                    resource = asyncPainterResource("https://image.tmdb.org/t/p/w300${episode.stillPath}"),
                    contentDescription = episode.name,
                    contentScale = ContentScale.Crop,
                    modifier = Modifier.fillMaxSize()
                )
            } else {
                Text(
                    text = "E${episode.episodeNumber}",
                    color = Color(0xFF555555),
                    fontWeight = FontWeight.Bold,
                    fontSize = 14.sp
                )
            }
        }

        // Episode info + watched button
        Row(modifier = Modifier.weight(1f), verticalAlignment = Alignment.Top) {
            Column(modifier = Modifier.weight(1f)) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    Text(
                        text = "E${episode.episodeNumber}",
                        color = BrandColors.Secondary,
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.Bold
                    )
                    if (episode.runtime != null && episode.runtime > 0) {
                        Text(
                            text = "·",
                            color = Color.Gray,
                            style = MaterialTheme.typography.labelMedium
                        )
                        Text(
                            text = "${episode.runtime}m",
                            color = Color.Gray,
                            style = MaterialTheme.typography.labelMedium
                        )
                    }
                    if (episode.voteAverage > 0.0) {
                        Text(
                            text = "·",
                            color = Color.Gray,
                            style = MaterialTheme.typography.labelMedium
                        )
                        Icon(
                            Icons.Filled.Star,
                            contentDescription = null,
                            tint = BrandColors.TextSecondary,
                            modifier = Modifier.size(10.dp)
                        )
                        Text(
                            text = (episode.voteAverage / 2).format1f(),
                            color = BrandColors.TextSecondary,
                            style = MaterialTheme.typography.labelMedium,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }

                Spacer(modifier = Modifier.height(2.dp))

                Text(
                    text = episode.name,
                    color = Color.White,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )

                if (episode.airDate.isNotBlank()) {
                    Spacer(modifier = Modifier.height(2.dp))
                    Text(
                        text = episode.airDate,
                        color = Color(0xFF666666),
                        style = MaterialTheme.typography.labelSmall
                    )
                }

                if (userRating > 0.0) {
                    Spacer(modifier = Modifier.height(2.dp))
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(2.dp)
                    ) {
                        Icon(
                            Icons.Filled.Star,
                            contentDescription = null,
                            tint = BrandColors.Primary,
                            modifier = Modifier.size(10.dp)
                        )
                        Text(
                            text = "You: ${userRating.format1f()}",
                            color = BrandColors.Primary,
                            style = MaterialTheme.typography.labelSmall,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }

                if (episode.overview.isNotBlank()) {
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        text = episode.overview,
                        color = Color.Gray,
                        style = MaterialTheme.typography.bodySmall,
                        lineHeight = 17.sp,
                        maxLines = 3,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }

            // Watched toggle
            Box(modifier = Modifier.size(32.dp), contentAlignment = Alignment.Center) {
                if (isLoading) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(20.dp),
                        strokeWidth = 2.dp,
                        color = BrandColors.Primary
                    )
                } else {
                    IconButton(
                        onClick = {
                            if (!isFuture) {
                                isLoading = true
                                onToggleWatched()
                            }
                        },
                        enabled = !isFuture,
                        modifier = Modifier.size(32.dp)
                    ) {
                        Icon(
                            imageVector = if (isWatched) Icons.Filled.CheckCircle else Icons.Outlined.CheckCircle,
                            contentDescription = if (isWatched) "Mark unwatched" else "Mark watched",
                            tint = when {
                                isFuture -> Color.Gray.copy(alpha = 0.3f)
                                isWatched -> BrandColors.Primary
                                else -> Color.Gray
                            },
                            modifier = Modifier.size(22.dp)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun TopBackButton(onBackClick: () -> Unit) {
    Box(
        modifier = Modifier
            .statusBarsPadding()
            .padding(4.dp)
    ) {
        IconButton(
            onClick = onBackClick,
            modifier = Modifier.background(Color.Black.copy(alpha = 0.5f), CircleShape)
        ) {
            Icon(
                imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                contentDescription = "Back",
                tint = Color.White
            )
        }
    }
}
